import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { getDelivery } from 'interfaces/delivery';
import { dateToFrench } from 'utils/date';

const PrintTourRoadmap = (props: { isExternalPage: boolean }) => {
    const navigate = useNavigate();
    const params = useParams();
    const [delivery, setDelivery] = useState<any>(null);
    const [orders, setOrders] = useState<any[]>([]);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [searchParams] = useSearchParams();
    const { isExternalPage } = props;

    const initOrders = async (deliveryId: string) => {
        const res = await getDelivery(deliveryId, isExternalPage);
        if (res) {
            document.title = `Feuille de route - tournée ${res.name ? res.name : ''}`;
            setDelivery(res);
            setOrders([...res.pendingOrders, ...res.deliveredOrders]);
            setTimeout(() => {
                window.print();
            }, 800);
        }
    };

    useEffect(() => {
        if (
            localStorage.getItem(
                (isExternalPage ? 'delivery-' : '') + 'token',
            ) === null
        ) {
            navigate('/login');
        }
        initOrders(params.deliveryId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getTotalWeight = (items: any[]) =>
        items
            ?.reduce(
                (acc: number, item: any) =>
                    acc + (item.brutWeight || 0) * item.quantity,
                0,
            )
            .toFixed(2) ?? '—';

    const getTotalPackages = (order: any) => {
        if (order.pcbTotal != null && order.pcbTotal !== '') return order.pcbTotal;
        return order.items?.reduce((acc: number, item: any) => acc + item.quantity, 0) ?? '—';
    };

    return (
        <div className="p-6 font-sans text-sm text-gray-800 print:p-4">
            {/* En-tête */}
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-toola-500">
                    Feuille de route
                </h1>
                {delivery && (
                    <p className="mt-1 text-base text-gray-600">
                        Tournée{' '}
                        <span className="font-semibold text-gray-800">
                            {delivery.name || '—'}
                        </span>{' '}
                        — {dateToFrench(delivery.deliveryDate)}
                    </p>
                )}
            </div>

            {/* Tableau */}
            <table className="w-full border-collapse border border-gray-300 text-sm">
                <thead>
                    <tr className="bg-gray-100 text-gray-700">
                        <th className="border border-gray-300 px-3 py-2 text-left w-[5%]">
                            N°
                        </th>
                        <th className="border border-gray-300 px-3 py-2 text-left w-[22%]">
                            Nom du client
                        </th>
                        <th className="border border-gray-300 px-3 py-2 text-left w-[28%]">
                            Adresse de livraison
                        </th>
                        <th className="border border-gray-300 px-3 py-2 text-center w-[12%]">
                            Poids (kg)
                        </th>
                        <th className="border border-gray-300 px-3 py-2 text-center w-[10%]">
                            Nb colis
                        </th>
                        <th className="border border-gray-300 px-3 py-2 text-center w-[11%]">
                            Chargé
                        </th>
                        <th className="border border-gray-300 px-3 py-2 text-center w-[11%]">
                            Livré
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {orders
                        .sort(
                            (a: any, b: any) =>
                                a.deliveryOrder - b.deliveryOrder,
                        )
                        .map((order: any, index: number) => {
                            const customerName =
                                order.customer?.displayName ||
                                order.customer?.company ||
                                '—';
                            const address = [
                                order.customer?.deliveryAddress,
                                [order.customer?.deliveryZipCode, order.customer?.deliveryCity]
                                    .filter(Boolean)
                                    .join(' '),
                            ]
                                .filter(Boolean)
                                .join(', ') || '—';
                            const weight = getTotalWeight(order.items);
                            const packages = getTotalPackages(order);
                            const deliveryNotes =
                                order.customer?.deliveryNotes || '';
                            const customerNotes = order.customerNotes || '';

                            return (
                                <tr
                                    key={order._id}
                                    className={
                                        index % 2 === 0
                                            ? 'bg-white'
                                            : 'bg-gray-50'
                                    }
                                >
                                    <td className="border border-gray-300 px-3 py-2 text-center font-medium">
                                        {index + 1}
                                    </td>
                                    <td className="border border-gray-300 px-3 py-2">
                                        <p className="font-semibold">
                                            {customerName}
                                        </p>
                                        {deliveryNotes && (
                                            <p className="mt-1 text-xs text-gray-500 italic">
                                                📦 {deliveryNotes}
                                            </p>
                                        )}
                                        {customerNotes && (
                                            <p className="mt-1 text-xs text-gray-500 italic">
                                                📝 {customerNotes}
                                            </p>
                                        )}
                                    </td>
                                    <td className="border border-gray-300 px-3 py-2 text-gray-700">
                                        {address}
                                    </td>
                                    <td className="border border-gray-300 px-3 py-2 text-center">
                                        {weight}
                                    </td>
                                    <td className="border border-gray-300 px-3 py-2 text-center">
                                        {packages}
                                    </td>
                                    <td className="border border-gray-300 px-3 py-2 text-center">
                                        <div className="flex justify-center">
                                            <div className="h-5 w-5 border-2 border-gray-400 rounded" />
                                        </div>
                                    </td>
                                    <td className="border border-gray-300 px-3 py-2 text-center">
                                        <div className="flex justify-center">
                                            <div className="h-5 w-5 border-2 border-gray-400 rounded" />
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                </tbody>
            </table>

            {/* Totaux */}
            {orders.length > 0 && (
                <div className="mt-4 flex gap-6 text-sm text-gray-600">
                    <p>
                        <span className="font-semibold">{orders.length}</span>{' '}
                        client{orders.length > 1 ? 's' : ''}
                    </p>
                    <p>
                        Poids total :{' '}
                        <span className="font-semibold">
                            {orders
                                .reduce(
                                    (acc, o) =>
                                        acc +
                                        (o.items?.reduce(
                                            (a: number, i: any) =>
                                                a +
                                                (i.brutWeight || 0) *
                                                    i.quantity,
                                            0,
                                        ) || 0),
                                    0,
                                )
                                .toFixed(2)}{' '}
                            kg
                        </span>
                    </p>
                    <p>
                        Nb colis total :{' '}
                        <span className="font-semibold">
                            {orders.reduce((acc, o) => {
                                if (o.pcbTotal != null && o.pcbTotal !== '') return acc + o.pcbTotal;
                                return acc + (o.items?.reduce((a: number, i: any) => a + i.quantity, 0) || 0);
                            }, 0)}
                        </span>
                    </p>
                </div>
            )}
        </div>
    );
};

export default PrintTourRoadmap;
